import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTripsToday, useBulkDeclineTrips, useBulkResetTrips, useMe, useConfigOperators } from '../lib/api';
import { CR_PHASE_LABELS } from '../lib/cr-phase.js';
import { getMruReasons, pushMruReason } from '../lib/mru-reasons';
import StatusBadge from '../components/StatusBadge.jsx';
import QuoteIdLink from '../components/QuoteIdLink.jsx';
import { isDuplicatePill } from '../lib/trs-pills.mjs';

const OPERATORS = ['sprintbach', 'raber', 'airstat', 'dondavis', 'midamericajet'];

// Trip ingestion sources. Until S85, the only source actually producing rows
// was 'avinode' (scraped from the Avinode UI). 'webhook' = broker-api,
// 'telegram' = @SentinelQuoteBot, 'email' = n8n Gmail bridge. Listing all four
// here so the dropdown is complete even when one is dormant.
const SOURCE_FILTERS = {
  ''                     : { label: 'All sources' },
  'avinode'              : { label: 'Avinode' },
  'webhook'              : { label: 'Broker API' },
  'telegram'             : { label: 'Telegram' },
  'email'                : { label: 'Email' },
  'centralized_request'  : { label: 'Centralized Request' },
};

// Per-source styling for the inline badge. Kept small to fit in a table cell.
const SOURCE_BADGE = {
  avinode:             'bg-slate-100 text-slate-700 border-slate-300',
  webhook:             'bg-tint-fill text-tint border-separator',
  telegram:            'bg-sky-50 text-sky-800 border-sky-200',
  email:               'bg-amber-50 text-amber-800 border-amber-200',
  centralized_request: 'bg-indigo-50 text-indigo-800 border-indigo-200',
};

function SourceBadge({ source }) {
  if (!source) return null;
  const label = SOURCE_FILTERS[source]?.label || source;
  const cls = SOURCE_BADGE[source] || 'bg-slate-100 text-slate-600 border-slate-300';
  return (
    <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded border ${cls}`}>
      {label}
    </span>
  );
}

// Flags a held trip whose flight date has already passed (dead/stale — server sets
// `departed`). Shown in the all-held view so ops can eyeball what's still recoverable.
function DepartedBadge({ departed }) {
  if (!departed) return null;
  return (
    <span
      className="inline-block text-[10px] px-1.5 py-0.5 rounded border bg-rose-50 text-rose-700 border-rose-200 ml-1 align-middle"
      title="Flight date already passed — dead/stale, not recoverable"
    >
      DEPARTED
    </span>
  );
}

// Short labels for the filter dropdown — full labels live in
// TripDetail.jsx PHASE2_DECISION_LABELS.
const PHASE2_DECISION_SHORT = {
  quotes_added:                       'Quotes added',
  no_flight_options:                  'No flight options',
  no_matching_fleet_group:            'No matching fleet group',
  no_matching_fleet_group_mixed:      'No matching fleet (mixed)',
  under_min_flight_time:              'Under min flight time',
  all_substituted_tails:              'All substituted tails',
  mixed_fleet_no_quotes:              'Mixed fleet, no quotes',
  excluded_domestic_airport:          'Excluded domestic airport',
  light_jet_excluded_airport:         'Light Jet excluded airport',
  no_international_operator:          'No international (policy)',
  asap_international_not_approved:    'ASAP international not approved',
  permit_lead_time_insufficient:      'Permit lead time insufficient',
  excluded_international_destination: 'Excluded international',
  airport_not_found:                  'Airport not found',
  tail_paused:                        'Tail paused',
  fleet_group_paused:                 'Fleet group paused',
};
function phase2Label(code) {
  return PHASE2_DECISION_SHORT[code] || code || '—';
}

// Intake-stage holds (Class A: save_disabled / unresolvable_airport / cust_create…)
// persist degraded metadata — a null date renders as the epoch '1970-01-01', an
// unparsed customer as '(unparsed)', and an unresolved airport as 'UNKN'. Render
// those placeholders as an em dash so a failed parse doesn't masquerade as real data.
const cleanField = (v) => {
  const s = String(v ?? '').trim();
  if (!s || s === '(unparsed)' || s.toUpperCase() === 'UNKN' || s.slice(0, 10) === '1970-01-01') return '—';
  return s;
};
const fmtRoute = (t) => {
  const rs = String(t.route_summary || '').trim();
  if (rs && rs.toUpperCase() !== 'UNKN→UNKN') return rs;
  const a = cleanField(t.departure_airport), b = cleanField(t.arrival_airport);
  return (a !== '—' || b !== '—') ? `${a}→${b}` : '—';
};

// Held-view row classes (server tags each held row via trips-query hold_class):
//   'quote'  — a live carted CR quote-hold (has a TRS verdict; sendable) → TRS-manageable
//   'intake' — a pre-quote intake/ops failure (degraded metadata) → not TRS-managed
const HOLD_CLASS_FILTERS = {
  ''      : { label: 'All held' },
  'quote' : { label: 'Quote holds (TRS)' },
  'intake': { label: 'Intake issues' },
};

// Trips that should NOT be bulk-action targets: already-terminal states.
// Operators can still click into Trip Detail to action these manually if needed.
function isActionable(trip) {
  if (trip.completed) return false;
  if (trip.do_not_post) return false;
  if (trip.status === 'completed') return false;
  return true;
}

// Status-filter dropdown options. "Held" is the headline use-case (operators
// reviewing what's waiting), but ops asked for a generalized status filter so
// other terminal states are surfaced too. Server returns badge.tone + label,
// so filtering is purely client-side and doesn't change the API contract.
const STATUS_FILTERS = {
  ''            : { label: 'All',                 match: () => true },
  // Stuck filter uses the server-computed badge tone+symbol so it stays in sync
  // with the badge logic in trips.js badgeFor(). Any trip with badge ⚠ red is
  // stuck in a stage longer than its threshold (Phase 1 data / Phase 2 / Phase 4).
  'stuck'       : { label: 'Stuck',               match: t => t.badge?.symbol === '⚠' && t.badge?.tone === 'red' },
  // Retry-loop: Phase 4 has failed N+ times for the same trip. Distinct from
  // generic "stuck" because the trip ISN'T idle — it's actively re-attempting
  // and failing each time. Server flags via badge symbol ↻.
  'retry_loop'  : { label: 'Retry loop (Phase 4)', match: t => t.badge?.symbol === '↻' },
  // Held fires for active trips with EITHER a quote-level hold (q.held) OR a
  // trip-level on_hold status (e.g. ops-hours hold from Phase 2 — S87).
  'held'        : { label: 'Held',                match: t => !t.do_not_post && !t.completed && t.status !== 'declined' && t.status !== 'completed' && (t.status === 'on_hold' || t.quotes?.some(q => q.held)) },
  'pending'     : { label: 'Pending review',      match: t => t.status === 'pending' && (t.quotes?.length || 0) > 0 && !t.quotes.some(q => q.held) },
  'no_quotes'   : { label: 'No quotes yet',       match: t => t.status === 'pending' && (t.quotes?.length || 0) === 0 },
  'posted'      : { label: 'Posted to Avinode',   match: t => t.avinode_response_confirmed === 'posted' },
  'declined_av' : { label: 'Declined on Avinode', match: t => t.avinode_response_confirmed === 'declined' },
  'declined'    : { label: 'Declined (intent)',   match: t => t.status === 'declined' && t.avinode_response_confirmed !== 'declined' },
  'do_not_post' : { label: 'Do Not Post',         match: t => !!t.do_not_post },
  'completed'   : { label: 'Completed',           match: t => t.status === 'completed' && !t.avinode_response_confirmed },
};

function BulkBar({ selected, onClear, onDecline, onReset, declining, resetting }) {
  if (selected.size === 0) return null;
  return (
    <div className="card p-3 flex items-center justify-between gap-3 text-sm bg-tint-fill border border-separator sticky top-2 z-10">
      <div>
        <strong>{selected.size}</strong> selected
        <button onClick={onClear} className="ml-3 text-xs text-slate-600 hover:text-slate-900 underline">
          Clear
        </button>
      </div>
      <div className="flex gap-2">
        <button
          onClick={onDecline}
          disabled={declining || resetting}
          className="px-3 py-1.5 rounded bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50 text-xs"
        >
          {declining ? 'Declining…' : `Decline ${selected.size}`}
        </button>
        <button
          onClick={onReset}
          disabled={declining || resetting}
          className="px-3 py-1.5 rounded bg-slate-200 text-slate-800 hover:bg-slate-300 disabled:opacity-50 text-xs"
        >
          {resetting ? 'Resetting…' : `Reset ${selected.size}`}
        </button>
      </div>
    </div>
  );
}

function BulkDeclineModal({ tripIds, onClose, onSubmit, isPending }) {
  const [reason, setReason] = useState('');
  const [force, setForce] = useState(false);
  const { data: meData } = useMe();
  const mruReasons = getMruReasons(meData?.user?.username);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
        <h2 className="font-semibold text-lg text-rose-900 mb-2">
          Decline {tripIds.length} trip{tripIds.length === 1 ? '' : 's'}?
        </h2>
        <div className="text-xs text-slate-600 mb-3">
          Trips: <span className="font-mono">{tripIds.slice(0, 5).join(', ')}{tripIds.length > 5 ? ` +${tripIds.length - 5} more` : ''}</span>
        </div>

        {mruReasons.length > 0 && (
          <div className="mb-3">
            <div className="text-xs text-slate-500 mb-1">Quick pick:</div>
            <div className="flex flex-wrap gap-1">
              {mruReasons.map(r => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setReason(r)}
                  className="text-xs px-2 py-1 rounded border border-rose-200 bg-white hover:bg-rose-100 text-rose-800"
                >
                  {r.length > 40 ? r.slice(0, 40) + '…' : r}
                </button>
              ))}
            </div>
          </div>
        )}

        <input
          autoFocus
          placeholder="Reason (applied to all selected trips)"
          className="w-full border-slate-300 rounded-md text-sm px-2 py-1 mb-2"
          value={reason}
          onChange={e => setReason(e.target.value)}
        />
        <label className="flex items-center gap-2 text-sm mb-3">
          <input type="checkbox" checked={force} onChange={e => setForce(e.target.checked)} />
          --force (bypass substitution guard)
        </label>

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded text-sm text-slate-700 hover:bg-slate-100"
          >Cancel</button>
          <button
            onClick={() => { pushMruReason(meData?.user?.username, reason); onSubmit({ reason, force }); }}
            disabled={isPending}
            className="px-4 py-1.5 rounded bg-rose-600 text-white text-sm hover:bg-rose-700 disabled:opacity-50"
          >
            {isPending ? 'Declining…' : `Confirm decline ${tripIds.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkResetModal({ tripIds, onClose, onSubmit, isPending }) {
  const [force, setForce] = useState(false);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
        <h2 className="font-semibold text-lg mb-2">
          Reset {tripIds.length} trip{tripIds.length === 1 ? '' : 's'}?
        </h2>
        <div className="text-xs text-slate-600 mb-3">
          Trips: <span className="font-mono">{tripIds.slice(0, 5).join(', ')}{tripIds.length > 5 ? ` +${tripIds.length - 5} more` : ''}</span>
          <br />Clears all 4 Phase 1 dedup sources for each trip. Only re-extracts if Avinode still shows trip as Unanswered.
        </div>
        <label className="flex items-center gap-2 text-sm mb-3">
          <input type="checkbox" checked={force} onChange={e => setForce(e.target.checked)} />
          --force (bypass "already posted to Avinode" safety check)
        </label>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded text-sm text-slate-700 hover:bg-slate-100">Cancel</button>
          <button
            onClick={() => onSubmit({ force })}
            disabled={isPending}
            className="px-4 py-1.5 rounded bg-sentinel-navy text-white text-sm hover:bg-sentinel-navy-700 disabled:opacity-50"
          >
            {isPending ? 'Resetting…' : `Confirm reset ${tripIds.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// TRS shadow-verdict pill for a held trip's Result cell (2026-07-22) — surfaces the solver's
// SEND/RECHECK/HOLD (+ the winning re-slot arrangement + disposition) so a send-able held row pops
// without opening the detail page. Prefers a SEND/ALREADY_LEGAL verdict; null when there's none.
function trsPill(trip) {
  const vs = trip.trs_verdicts || [];
  if (!vs.length) return null;
  const v = vs.find(x => x.verdict === 'SEND' || x.verdict === 'ALREADY_LEGAL') || vs[0];
  if (!v || !v.verdict) return null;
  const isSend = v.verdict === 'SEND' || v.verdict === 'ALREADY_LEGAL';
  const pill = v.disposition?.pill;
  const isDup = isDuplicatePill(pill);   // SQP-297: already delivered — not waiting to go, so never green
  const cls = isDup
    ? 'text-slate-600 bg-slate-100 border-slate-300'
    : isSend
    ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
    : v.verdict === 'RECHECK'
      ? 'text-amber-700 bg-amber-50 border-amber-200'
      : 'text-slate-600 bg-slate-50 border-slate-200';
  return (
    <div
      className={`inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded border text-[10px] ${cls}`}
      title={`TRS ${v.verdict}${v.arrangement ? ' · ' + v.arrangement : ''}${pill ? ' · ' + pill : ''}`}
    >
      <span className="font-semibold">{isSend && !isDup ? '✅ ' : ''}{v.verdict}</span>
      {v.arrangement && <span className="opacity-80">· {v.arrangement}</span>}
      {pill && pill !== 'Pending' && <span className="opacity-70">({pill})</span>}
    </div>
  );
}

export default function TodaysTrips() {
  // Defaults chosen for the job this page is actually used for — working the held queue (Yves,
  // 2026-08-07). 'held' surfaces every held trip regardless of age; the old 24h default showed 14
  // of 109 and made the backlog look far smaller than it is. It also unlocks the Hide-departed and
  // Hold-type controls, which only render in held mode.
  const [selectedOps, setSelectedOps] = useState(OPERATORS);
  const [windowMode, setWindowMode] = useState('held');   // hours (number) or 'held' (all held, any age)
  // Deselect PAUSED operators once the config lands. Keyed on quoting_paused, NOT `active` — every
  // operator carries ACTIVE=1, so `active` would select everyone. Applied once (opsDefaulted) so a
  // later refetch can never stamp over a selection the user has since changed by hand.
  const [opsDefaulted, setOpsDefaulted] = useState(false);
  const { data: opCfg } = useConfigOperators();
  useEffect(() => {
    if (opsDefaulted || !opCfg?.operators?.length) return;
    const live = OPERATORS.filter(op => {
      const row = opCfg.operators.find(o => o.operator_id === op);
      return row ? row.quoting_paused !== true : true;   // unknown ⇒ keep selected, never hide work
    });
    if (live.length) setSelectedOps(live);               // all paused ⇒ leave as-is rather than blank
    setOpsDefaulted(true);
  }, [opCfg, opsDefaulted]);
  const [hideDeparted, setHideDeparted] = useState(false);
  const [phase2Filter, setPhase2Filter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [holdClassFilter, setHoldClassFilter] = useState('');   // held mode: '', 'quote', 'intake'
  // null = server order (created_at DESC, departed sunk); 'asc'|'desc' = sort by departure date.
  const [dateSort, setDateSort] = useState(null);
  const [query, setQuery] = useState('');                       // free-text search (TRS Verdicts parity)
  const [selected, setSelected] = useState(new Set());
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const heldAll = windowMode === 'held';
  const operator = selectedOps.join(',');
  const { data, isLoading, isError, error } = useTripsToday({
    operator,
    hours: heldAll ? undefined : windowMode,
    held: heldAll ? 'all' : undefined,
    phase2Decision: phase2Filter || null,
    limit: 500,
  });

  const bulkDecline = useBulkDeclineTrips();
  const bulkReset = useBulkResetTrips();

  function toggleOp(op) {
    setSelectedOps(s => s.includes(op) ? s.filter(o => o !== op) : [...s, op]);
  }

  // Apply status + source filters client-side. Returns a stable reference so
  // memoized downstream lists (actionableKeys, displayed rows) don't thrash.
  const filteredTrips = useMemo(() => {
    const trips = data?.trips || [];
    const statusMatcher = STATUS_FILTERS[statusFilter]?.match || STATUS_FILTERS[''].match;
    const q = query.trim().toLowerCase();
    const out = trips.filter(t => {
      if (sourceFilter && t.source !== sourceFilter) return false;
      // OPERATOR SCOPING IN HELD MODE (2026-08-07). The server deliberately does NOT scope the held
      // view by operator — that is what surfaces `eas` CR holds, which have no button here
      // (tests/trips-query.test.js pins it: "held: does NOT scope by operator"). So the buttons did
      // nothing in held mode, which only became visible once 'All held' became the default window.
      //
      // Filter client-side, and ONLY for operators that HAVE a button: a row whose operator is not
      // in OPERATORS (eas) is never hidden, so the "nothing is scoped away" guarantee still holds
      // for the operators the user cannot see or select. Deselecting SprintBach now hides
      // SprintBach and nothing else.
      if (heldAll && OPERATORS.includes(t.operator_id) && !selectedOps.includes(t.operator_id)) return false;
      if (heldAll && holdClassFilter && t.hold_class !== holdClassFilter) return false;
      if (hideDeparted && t.departed) return false;
      if (q) {
        // Same spirit as TrsVerdicts' search: match anything a human would use to find the row —
        // trip id, operator, customer, route/airports, quote ids, tails.
        const hay = [
          t.trip_id, t.operator_id, t.customer_name, t.route_summary,
          t.departure_airport, t.arrival_airport, t.hold_reason,
          // 2026-08-08: this read `x.primary_aircraft_tail`, but the API serialises that field
          // as `tail` (routes/trips.js). It was therefore ALWAYS undefined and filtered out, so
          // searching this list by tail number silently matched nothing — despite the comment
          // above promising tails are searchable. Price added for the same reason.
          ...(t.quotes || []).flatMap(x => [x.sentinel_quote_id, x.tail, x.price]),
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return statusMatcher(t);
    });
    // Explicit DATE sort wins when the user asks for it. Rows with no departure date sort LAST in
    // both directions — a missing date is unknown, not "earliest", and floating them to the top of
    // an ascending sort would bury the imminent departures this view exists to surface. Compared as
    // 'YYYY-MM-DD' strings, which is what the column renders and what the server stores; the epoch
    // artifacts (1970-01-01) sort early and are visibly wrong rather than silently reordered.
    if (dateSort) {
      const key = (t) => {
        const d = t.departure_date ? String(t.departure_date).slice(0, 10) : '';
        return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : null;
      };
      return [...out].sort((a, b) => {
        const ka = key(a), kb = key(b);
        if (ka === null && kb === null) return 0;
        if (ka === null) return 1;            // undated last, always
        if (kb === null) return -1;
        return dateSort === 'asc' ? ka.localeCompare(kb) : kb.localeCompare(ka);
      });
    }
    // In the all-held view, sink departed (dead) trips to the bottom so the still-
    // actionable ones stay on top; server order (created_at DESC) is otherwise kept.
    return heldAll ? [...out].sort((a, b) => (a.departed ? 1 : 0) - (b.departed ? 1 : 0)) : out;
  }, [data, statusFilter, sourceFilter, holdClassFilter, hideDeparted, heldAll, query, dateSort, selectedOps]);

  // S98: selection keys are (trip_id, operator_id) composites — the same Avinode
  // trip_id exists for MULTIPLE operators (H557YZ class), so keying on bare
  // trip_id selected/actioned both operators' rows at once.
  const tripKey = t => `${t.trip_id}|${t.operator_id}`;
  const keyToTrip = key => {
    const i = key.lastIndexOf('|');
    return { trip_id: key.slice(0, i), operator: key.slice(i + 1) };
  };

  const actionableKeys = useMemo(
    () => filteredTrips.filter(isActionable).map(tripKey),
    [filteredTrips]
  );

  function toggleTrip(key) {
    setSelected(s => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  function toggleSelectAll() {
    if (actionableKeys.every(k => selected.has(k))) setSelected(new Set());
    else setSelected(new Set(actionableKeys));
  }
  const allSelected = actionableKeys.length > 0 && actionableKeys.every(k => selected.has(k));

  async function doBulkDecline({ reason, force }) {
    const trips = Array.from(selected).map(keyToTrip);
    const r = await bulkDecline.mutateAsync({ trips, reason, force });
    setShowDeclineModal(false);
    setSelected(new Set());
    setBulkResult({ action: 'decline', ...r });
  }
  async function doBulkReset({ force }) {
    const trips = Array.from(selected).map(keyToTrip);
    const r = await bulkReset.mutateAsync({ trips, force });
    setShowResetModal(false);
    setSelected(new Set());
    setBulkResult({ action: 'reset', ...r });
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="card p-3 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-600">Operator:</span>
          {OPERATORS.map(op => (
            <button
              key={op}
              onClick={() => toggleOp(op)}
              className={`px-2.5 py-1 text-xs rounded-md border capitalize transition-colors ${
                selectedOps.includes(op)
                  ? 'bg-sentinel-navy text-white border-sentinel-navy'
                  : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
              }`}
            >
              {op}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-600">Window:</span>
          <select
            value={windowMode}
            onChange={e => { const v = e.target.value; setWindowMode(v === 'held' ? 'held' : Number(v)); setSelected(new Set()); }}
            className="text-sm border border-slate-300 rounded-md px-2 py-1"
          >
            <option value={6}>Last 6h</option>
            <option value={24}>Last 24h</option>
            <option value={48}>Last 48h</option>
            <option value={168}>Last week</option>
            <option value="held">All held (any age)</option>
          </select>
        </div>
        {heldAll && (
          <label className="flex items-center gap-1.5 text-sm text-slate-600" title="Departed trips (flight date already passed) are shown with a badge and sunk to the bottom; check to hide them.">
            <input
              type="checkbox"
              checked={hideDeparted}
              onChange={e => setHideDeparted(e.target.checked)}
              className="rounded border-slate-300"
            />
            Hide departed
          </label>
        )}
        {heldAll && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-600">Hold type:</span>
            <select
              value={holdClassFilter}
              onChange={e => { setHoldClassFilter(e.target.value); setSelected(new Set()); }}
              className="text-sm border border-slate-300 rounded-md px-2 py-1"
              title="Quote holds are carted CR quotes with a TRS crew verdict (manageable). Intake issues are pre-quote intake failures."
            >
              {Object.entries(HOLD_CLASS_FILTERS).map(([key, { label }]) => {
                const n = key === ''
                  ? (data?.trips?.length || 0)
                  : (data?.trips || []).filter(t => t.hold_class === key).length;
                return <option key={key} value={key}>{label}{key !== '' ? ` (${n})` : ''}</option>;
              })}
            </select>
          </div>
        )}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-600">Status:</span>
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setSelected(new Set()); }}
            className="text-sm border border-slate-300 rounded-md px-2 py-1"
          >
            {Object.entries(STATUS_FILTERS).map(([key, { label, match }]) => {
              const n = key === '' ? (data?.trips?.length || 0) : (data?.trips || []).filter(match).length;
              return <option key={key} value={key}>{label}{key !== '' ? ` (${n})` : ''}</option>;
            })}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-600">Source:</span>
          <select
            value={sourceFilter}
            onChange={e => { setSourceFilter(e.target.value); setSelected(new Set()); }}
            className="text-sm border border-slate-300 rounded-md px-2 py-1"
          >
            {Object.entries(SOURCE_FILTERS).map(([key, { label }]) => {
              const n = key === ''
                ? (data?.trips?.length || 0)
                : (data?.trips || []).filter(t => t.source === key).length;
              return <option key={key} value={key}>{label}{key !== '' ? ` (${n})` : ''}</option>;
            })}
          </select>
        </div>
        {/* Phase 2 is an AVINODE-flow concept — CR trips never populate phase2_decision (measured
            2026-08-07: 109/109 held CR trips NULL, 18 of 127 held trips overall had any value). On
            the held view this dropdown offered only "All", so it is rendered only when the current
            result set actually contains decisions to filter by. */}
        {(data?.phase2_decision_counts || []).length > 0 && (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-600">Phase 2 result:</span>
          <select
            value={phase2Filter}
            onChange={e => setPhase2Filter(e.target.value)}
            className="text-sm border border-slate-300 rounded-md px-2 py-1"
          >
            <option value="">All</option>
            {(data?.phase2_decision_counts || []).map(({ decision, n }) => (
              <option key={decision} value={decision}>
                {phase2Label(decision)} ({n})
              </option>
            ))}
          </select>
        </div>
        )}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-600">Search:</span>
          <input
            type="search"
            value={query}
            onChange={e => { setQuery(e.target.value); setSelected(new Set()); }}
            placeholder="trip / quote / tail / customer…"
            className="text-sm border border-slate-300 rounded-md px-2 py-1 w-56 focus:outline-none focus:ring-2 focus:ring-tint"
            aria-label="Search trips"
          />
          {query && (
            <button className="text-xs text-slate-500 hover:text-slate-700" onClick={() => setQuery('')}>✕ clear</button>
          )}
        </div>
        {data && (
          <div className="ml-auto text-sm text-slate-500">
            {statusFilter
              ? <>{filteredTrips.length} of {data.count} trips · {actionableKeys.length} actionable</>
              : <>{data.count} trips · {actionableKeys.length} actionable</>
            }
          </div>
        )}
      </div>

      {/* Bulk action bar */}
      <BulkBar
        selected={selected}
        onClear={() => setSelected(new Set())}
        onDecline={() => setShowDeclineModal(true)}
        onReset={() => setShowResetModal(true)}
        declining={bulkDecline.isPending}
        resetting={bulkReset.isPending}
      />

      {/* Bulk result banner */}
      {bulkResult && (
        <div className={`card p-3 text-sm ${bulkResult.successful === bulkResult.requested ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="flex items-center justify-between gap-3">
            <div>
              Bulk {bulkResult.action}: <strong>{bulkResult.successful}/{bulkResult.requested}</strong> succeeded
              {bulkResult.successful < bulkResult.requested && (
                <span className="ml-2 text-xs text-slate-600">
                  Failed: {bulkResult.results.filter(r => !r.ok).map(r => r.trip_id).slice(0, 5).join(', ')}
                </span>
              )}
            </div>
            <button onClick={() => setBulkResult(null)} className="text-slate-400 hover:text-slate-700">×</button>
          </div>
        </div>
      )}

      {isLoading && <div className="text-slate-500">Loading…</div>}
      {isError && <div className="text-rose-700">Error: {error.message}</div>}

      {data?.trips?.length === 0 && (
        <div className="card p-6 text-center text-slate-500">No trips in window.</div>
      )}

      {data?.trips?.length > 0 && filteredTrips.length === 0 && (
        <div className="card p-6 text-center text-slate-500">
          No trips match the <strong>{STATUS_FILTERS[statusFilter]?.label}</strong> filter.{' '}
          <button onClick={() => setStatusFilter('')} className="text-sentinel-navy hover:underline">Clear filter</button>
        </div>
      )}

      {filteredTrips.length > 0 && (
        <>
          {/* Desktop table */}
          <div className="hidden md:block card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr className="text-left text-xs uppercase text-slate-500 tracking-wider">
                  <th className="px-3 py-2 w-8">
                    {actionableKeys.length > 0 && (
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                        aria-label="Select all actionable trips"
                        className="cursor-pointer"
                      />
                    )}
                  </th>
                  <th className="px-3 py-2">Trip</th>
                  <th className="px-3 py-2">Op</th>
                  <th className="px-3 py-2">Source</th>
                  <th className="px-3 py-2">Route</th>
                  <th className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => setDateSort(d => (d === 'asc' ? 'desc' : d === 'desc' ? null : 'asc'))}
                      className="inline-flex items-center gap-1 hover:text-slate-900"
                      title="Sort by departure date — click again to reverse, a third time to return to default order"
                    >
                      Date
                      <span className="text-[10px] text-slate-400">
                        {dateSort === 'asc' ? '▲' : dateSort === 'desc' ? '▼' : '⇅'}
                      </span>
                    </button>
                  </th>
                  <th className="px-3 py-2">Customer</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Result</th>
                  <th className="px-3 py-2">Quote IDs</th>
                </tr>
              </thead>
              <tbody>
                {filteredTrips.map(t => {
                  const actionable = isActionable(t);
                  return (
                    <tr key={t.id} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="px-3 py-2">
                        {actionable && (
                          <input
                            type="checkbox"
                            checked={selected.has(tripKey(t))}
                            onChange={() => toggleTrip(tripKey(t))}
                            aria-label={`Select trip ${t.trip_id} (${t.operator_id})`}
                            className="cursor-pointer"
                          />
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono">
                        <Link className="text-sentinel-navy hover:underline" to={`/trips/${t.trip_id}?operator=${encodeURIComponent(t.operator_id)}`}>
                          {t.trip_id}
                        </Link>
                      </td>
                      <td className="px-3 py-2 capitalize text-slate-600">{t.operator_id}</td>
                      <td className="px-3 py-2"><SourceBadge source={t.source} /></td>
                      <td className="px-3 py-2 font-mono text-xs">
                        {fmtRoute(t)}
                        {t.leg_count > 1 && <span className="ml-1 text-[10px] text-slate-400">({t.leg_count} legs)</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600">{cleanField(t.departure_date)}<DepartedBadge departed={t.departed} /></td>
                      <td className="px-3 py-2 text-xs">{cleanField(t.customer_name)}</td>
                      <td className="px-3 py-2"><StatusBadge badge={t.badge} /></td>
                      <td className="px-3 py-2 text-xs text-slate-600">
                        {t.source === 'centralized_request' && t.hold_reason ? (
                          /* CR result: cr_phase bucket as the main status, hold_reason as the sub-reason
                             (same data TripDetail's "CR close reason" panel shows). */
                          <span title={t.hold_reason}>
                            {CR_PHASE_LABELS[t.cr_phase] || t.cr_phase || 'CR resolved'}
                            <div className="text-[10px] text-rose-700 mt-0.5 font-mono">
                              {t.hold_reason.length > 44 ? t.hold_reason.slice(0, 44) + '…' : t.hold_reason}
                            </div>
                          </span>
                        ) : t.phase2_decision ? (
                          <span title={t.phase2_decision_note || ''}>{phase2Label(t.phase2_decision)}</span>
                        ) : '—'}
                        {t.operator_decline_reason && (
                          <div className="text-[10px] text-amber-700 mt-0.5" title="Operator decline reason">
                            ⚠ {t.operator_decline_reason.length > 30 ? t.operator_decline_reason.slice(0, 30) + '…' : t.operator_decline_reason}
                          </div>
                        )}
                        {trsPill(t)}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">
                        {t.quotes.length === 0 ? '—' : (
                          <span className="flex flex-wrap gap-x-2 gap-y-0.5">
                            {t.quotes.map(q => (
                              <span key={q.sentinel_quote_id} title={q.pe?.challenger?.reason || q.pe?.champion?.reason || ''}>
                                <QuoteIdLink id={q.sentinel_quote_id} url={q.sentinel_url} />
                                {/* 2026-08-08: the quoted price was never rendered here — the API has
                                    always sent it (routes/trips.js `price: q.final_price`). Without it
                                    a trip carrying DUPLICATE quotes showed several quote ids and no way
                                    to compare them, which is the exact comparison a reviewer needs. */}
                                {Number.isFinite(q.price) && q.price > 0 ? (
                                  <span className="text-slate-600"> ${q.price.toLocaleString()}</span>
                                ) : null}
                                {q.pe?.delta ? (
                                  <span className={q.pe.delta > 0 ? 'text-amber-600' : 'text-sky-600'}>
                                    {' '}{q.pe.delta > 0 ? '+' : '−'}${Math.abs(q.pe.delta).toLocaleString()}
                                  </span>
                                ) : null}
                                {q.booked?.booked ? <span className="text-emerald-700" title={q.booked.status}> ✓bk</span> : null}
                              </span>
                            ))}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-2">
            {filteredTrips.map(t => {
              const actionable = isActionable(t);
              return (
                <div key={t.id} className="card p-3">
                  <div className="flex items-start gap-2">
                    {actionable && (
                      <input
                        type="checkbox"
                        checked={selected.has(tripKey(t))}
                        onChange={() => toggleTrip(tripKey(t))}
                        aria-label={`Select trip ${t.trip_id} (${t.operator_id})`}
                        className="mt-1 cursor-pointer flex-shrink-0"
                      />
                    )}
                    <Link to={`/trips/${t.trip_id}?operator=${encodeURIComponent(t.operator_id)}`} className="flex-1 block hover:opacity-80">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="font-mono font-semibold text-sentinel-navy">{t.trip_id}</div>
                          <div className="text-xs text-slate-500 capitalize flex items-center gap-1.5">
                            <span>{t.operator_id}</span>
                            <SourceBadge source={t.source} />
                            <span>· {cleanField(t.customer_name)}</span>
                          </div>
                        </div>
                        <StatusBadge badge={t.badge} />
                      </div>
                      <div className="mt-2 text-sm font-mono">{fmtRoute(t)}</div>
                      <div className="text-xs text-slate-500">{cleanField(t.departure_date)}{t.leg_count > 1 && ` · ${t.leg_count} legs`}<DepartedBadge departed={t.departed} /></div>
                      {t.source === 'centralized_request' && t.hold_reason ? (
                        <div className="mt-1 text-xs text-slate-600">
                          {CR_PHASE_LABELS[t.cr_phase] || t.cr_phase || 'CR resolved'}
                          <div className="text-[11px] text-rose-700 font-mono">{t.hold_reason}</div>
                        </div>
                      ) : t.phase2_decision && (
                        <div className="mt-1 text-xs text-slate-600">
                          <span className="text-slate-400">Result: </span>
                          {phase2Label(t.phase2_decision)}
                        </div>
                      )}
                      {t.operator_decline_reason && (
                        <div className="text-xs text-amber-700 mt-0.5">⚠ {t.operator_decline_reason}</div>
                      )}
                      {t.quotes.length > 0 && (
                        <div className="mt-1 text-xs font-mono text-slate-600 flex flex-wrap gap-x-2">
                          {t.quotes.map(q => (
                            <span key={q.sentinel_quote_id}>
                              <QuoteIdLink id={q.sentinel_quote_id} url={q.sentinel_url} />
                              {/* price — mirrors the desktop table (2026-08-08) */}
                              {Number.isFinite(q.price) && q.price > 0 ? (
                                <span> ${q.price.toLocaleString()}</span>
                              ) : null}
                              {q.pe?.delta ? (
                                <span className={q.pe.delta > 0 ? 'text-amber-600' : 'text-sky-600'}>
                                  {' '}{q.pe.delta > 0 ? '+' : '−'}${Math.abs(q.pe.delta).toLocaleString()}
                                </span>
                              ) : null}
                              {q.booked?.booked ? <span className="text-emerald-700"> ✓bk</span> : null}
                            </span>
                          ))}
                        </div>
                      )}
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Modals — display "TRIP (operator)" so colliding trip_ids are unambiguous */}
      {showDeclineModal && (
        <BulkDeclineModal
          tripIds={Array.from(selected).map(k => { const t = keyToTrip(k); return `${t.trip_id} (${t.operator})`; })}
          onClose={() => setShowDeclineModal(false)}
          onSubmit={doBulkDecline}
          isPending={bulkDecline.isPending}
        />
      )}
      {showResetModal && (
        <BulkResetModal
          tripIds={Array.from(selected).map(k => { const t = keyToTrip(k); return `${t.trip_id} (${t.operator})`; })}
          onClose={() => setShowResetModal(false)}
          onSubmit={doBulkReset}
          isPending={bulkReset.isPending}
        />
      )}
    </div>
  );
}
